import {
  PROSPECT_COMPANIES_QUERY,
  TRIAGE_QUEUE_QUERY,
  UPSERT_PROSPECT_COMPANY,
} from "@lib/graphql/outreach";
import { useOutreachListChannel } from "@lib/hooks/useOutreachListChannel";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { useState } from "react";
import { useMutation, useQuery } from "urql";
import { JobPostingEvidenceModal } from "./JobPostingEvidenceModal";
import type { JobPosting, ProspectCompany } from "./types";

export function TriageTab() {
  const {
    nodes: postings,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ triageQueue: Connection<JobPosting> }, JobPosting>({
    query: TRIAGE_QUEUE_QUERY,
    getConnection: (data) => data.triageQueue,
  });
  useOutreachListChannel(refresh);

  if (fetching && postings.length === 0) {
    return <p className="text-sm text-text-muted">Loading triage queue…</p>;
  }

  if (postings.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
        <p className="text-sm text-text-muted">Nothing to triage right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {postings.map((p) => (
        <TriageRow key={p.id} posting={p} onChanged={refresh} />
      ))}
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

function TriageRow({ posting, onChanged }: { posting: JobPosting; onChanged: () => void }) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [companyName, setCompanyName] = useState(posting.companyName ?? "");
  const [busy, setBusy] = useState(false);
  // Company picker: one large page (field max_page_size 250), not paginated.
  const [{ data: companiesData }] = useQuery({
    query: PROSPECT_COMPANIES_QUERY,
    variables: { first: 250 },
  });
  const [, upsertCompany] = useMutation(UPSERT_PROSPECT_COMPANY);

  const companies: ProspectCompany[] = (companiesData?.prospectCompanies?.edges ?? []).map(
    (edge: { node: ProspectCompany }) => edge.node,
  );
  const suggestions = companyName
    ? companies.filter((c) => c.name.toLowerCase().includes(companyName.toLowerCase())).slice(0, 5)
    : [];

  const handleConfirm = async () => {
    if (!companyName.trim()) return;
    setBusy(true);
    await upsertCompany({ input: { name: companyName.trim() } });
    onChanged();
    setBusy(false);
    setResolving(false);
  };

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">
            {posting.title ?? "Untitled posting"}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">
            {posting.posterName ? `Posted by ${posting.posterName}` : "Unknown poster"}
            {posting.posterCompany ? ` · ${posting.posterCompany}` : ""}
            {posting.location ? ` · ${posting.location}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setEvidenceOpen(true)}
            className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:bg-amber-400/20 dark:text-amber-200"
          >
            Signal {posting.agencyLikelihoodScore ?? "—"}/100
          </button>
        </div>
      </div>

      {resolving ? (
        <div className="mt-3 space-y-2">
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Hiring company name"
            className="block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm"
          />
          {suggestions.length > 0 && (
            <ul className="divide-y divide-border rounded-md border border-input-border text-sm">
              {suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setCompanyName(c.name)}
                    className="block w-full px-3 py-2 text-left text-text-secondary hover:bg-card-hover"
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleConfirm}
              disabled={busy || !companyName.trim()}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Confirm company
            </button>
            <button
              onClick={() => setResolving(false)}
              className="rounded-md border border-input-border px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setResolving(true)}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover"
          >
            Confirm hiring company
          </button>
          <a
            href={posting.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-text-muted hover:text-accent"
          >
            Open on LinkedIn
          </a>
        </div>
      )}

      {posting.rawText && (
        <JobPostingEvidenceModal
          open={evidenceOpen}
          onClose={() => setEvidenceOpen(false)}
          title={posting.title ?? "Job posting"}
          rawText={posting.rawText}
          evidence={posting.evidence}
          score={posting.agencyLikelihoodScore}
          posterName={posting.posterName}
          posterCompany={posting.posterCompany}
        />
      )}
    </div>
  );
}
